<template>
  <v-app>
    <v-app-bar class="app-header">
      <v-app-bar-title class="app-title"> Collector </v-app-bar-title>

      <v-spacer />

      <!-- Demo mode indicator -->
      <v-chip
        v-if="isDemoMode"
        color="warning"
        size="small"
        variant="tonal"
        class="mr-2"
        data-testid="demo-mode-chip"
      >
        Demo
      </v-chip>

      <!-- Navigation links -->
      <nav class="nav-links" aria-label="Main navigation">
        <v-btn variant="text" size="small" :to="{ name: 'shares' }">
          <v-icon start size="18"> mdi-link-variant </v-icon>
          Shares
        </v-btn>
        <v-btn variant="text" size="small" :to="{ name: 'admin' }">
          <v-icon start size="18"> mdi-chart-line </v-icon>
          Admin
        </v-btn>
      </nav>

      <!-- Dark mode toggle -->
      <v-btn
        icon
        variant="text"
        :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        class="theme-toggle"
        @click="toggleTheme"
      >
        <v-icon>{{ isDark ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
      </v-btn>

      <!-- User menu (prod mode + authenticated) -->
      <v-menu v-if="!isDemoMode && auth.isAuthenticated" location="bottom end">
        <template #activator="{ props: menuProps }">
          <v-btn
            icon
            variant="text"
            v-bind="menuProps"
            data-testid="user-menu-btn"
            :aria-label="`User menu for ${auth.user?.displayName ?? 'user'}`"
          >
            <v-avatar
              v-if="auth.user?.avatarUrl"
              :image="auth.user.avatarUrl"
              size="32"
              data-testid="user-avatar"
            />
            <v-icon v-else size="28"> mdi-account-circle </v-icon>
          </v-btn>
        </template>
        <v-list density="compact" min-width="180">
          <v-list-item>
            <v-list-item-title class="font-weight-medium" data-testid="user-display-name">
              {{ auth.user?.displayName ?? 'Account' }}
            </v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item
            prepend-icon="mdi-logout"
            title="Sign out"
            data-testid="signout-btn"
            @click="handleSignOut"
          />
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Syncing state -->
    <v-banner
      v-if="isSyncing"
      color="info"
      icon="mdi-cloud-sync-outline"
      lines="one"
      data-testid="sync-progress-banner"
    >
      <v-banner-text>
        Syncing {{ syncProgress.current }} of {{ syncProgress.total }} changes...
      </v-banner-text>
    </v-banner>

    <!-- Success state (auto-dismiss) -->
    <v-banner
      v-else-if="syncResult === 'success'"
      color="success"
      icon="mdi-cloud-check-outline"
      lines="one"
      data-testid="sync-success-banner"
    >
      <v-banner-text>All changes synced</v-banner-text>
    </v-banner>

    <!-- Partial failure state -->
    <v-banner
      v-else-if="syncResult === 'partial-failure'"
      color="error"
      icon="mdi-cloud-alert"
      lines="one"
      data-testid="sync-error-banner"
    >
      <v-banner-text>
        {{ failedCount }} {{ failedCount === 1 ? 'change' : 'changes' }} failed to sync
      </v-banner-text>
      <template #actions>
        <v-btn variant="text" size="small" data-testid="retry-btn" @click="retryFailed">
          Retry
        </v-btn>
        <v-btn variant="text" size="small" data-testid="discard-btn" @click="discardFailed">
          Discard
        </v-btn>
      </template>
    </v-banner>

    <!-- Offline state -->
    <v-banner
      v-else-if="isOffline"
      color="warning"
      icon="mdi-wifi-off"
      lines="one"
      class="offline-banner"
      data-testid="offline-banner"
    >
      <v-banner-text>
        You are offline. Changes will be saved locally and synced when you reconnect.
        <span v-if="pendingCount > 0" data-testid="pending-count"
          >({{ pendingCount }} pending)</span
        >
      </v-banner-text>
    </v-banner>

    <v-main>
      <v-container fluid class="main-content">
        <slot />
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useThemeToggle } from '@/composables/useTheme'
import { useOnlineStatus } from '@/composables/useOnlineStatus'
import { useOfflineQueue } from '@/composables/useOfflineQueue'
import { useSyncEngine } from '@/composables/useSyncEngine'
import { useConfigStore } from '@/stores/config'
import { useAuthStore } from '@/stores/auth'

const { initTheme, toggleTheme, isDark } = useThemeToggle()
const { isOffline } = useOnlineStatus()
const { pendingCount } = useOfflineQueue()
const { isSyncing, syncProgress, syncResult, failedCount, retryFailed, discardFailed } =
  useSyncEngine()
const config = useConfigStore()
const auth = useAuthStore()
const router = useRouter()
const isDemoMode = computed(() => config.appMode === 'demo')

// Initialize theme on mount
onMounted(() => {
  initTheme()
})

async function handleSignOut() {
  await auth.logout()
  router.push({ name: 'login' })
}
</script>

<style scoped>
.app-header {
  border-bottom: 1px solid rgb(var(--v-theme-surface-variant));
}

.app-title {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 1.25rem;
  letter-spacing: -0.01em;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 8px;
}

.theme-toggle {
  min-width: 44px;
  min-height: 44px;
}

.main-content {
  padding: 24px; /* md spacing token */
}

/* Generous whitespace for museum feel */
@media (min-width: 960px) {
  .main-content {
    padding: 36px; /* lg spacing token */
  }
}

/* Focus states for accessibility */
.theme-toggle:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
